// @ts-nocheck
import React, { useMemo, useState, type ComponentType, type SVGProps } from 'react';
import { Phone, Calendar, Clock, Users, TrendingUp, BarChart3, ListChecks } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { hasMeetingBooked } from '@/utils/callUtils';
import { useTranslation } from 'react-i18next';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { currentLocale, formatNumber, formatShortDate } from '@/i18n/locale';
import ListSelect from '@/components/analytics/ListSelect';
import { CONNECTED_OUTCOMES, callsOfList, listPerformance, type ListPerformanceRow } from '@/lib/listPerformance';

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

interface OutcomeMeta {
  badgeClass: string;
}

interface CallShape {
  id?: string;
  call_outcome?: string | null;
  prospect_name?: string | null;
  prospect_company?: string | null;
  duration_seconds?: number | null;
  started_at?: string | Date | null;
  created_date?: string | Date | null;
}

interface ProspectShape {
  id?: string;
  prospect_phone?: string | null;
}

const OUTCOME_CONFIG: Record<string, OutcomeMeta> = {
    meeting_booked:     { badgeClass: 'bg-green-100 text-green-700 border-green-200' },
    contacted:          { badgeClass: 'bg-blue-100 text-blue-700 border-blue-200' },
    callback_requested: { badgeClass: 'bg-orange-100 text-orange-700 border-orange-200' },
    not_interested:     { badgeClass: 'bg-slate-100 text-slate-600 border-slate-200' },
    not_reachable:      { badgeClass: 'bg-slate-100 text-slate-500 border-slate-200' },
    left_voicemail:     { badgeClass: 'bg-purple-100 text-purple-700 border-purple-200' },
    cancelled:          { badgeClass: 'bg-red-100 text-red-600 border-red-200' },
    sms_sent:           { badgeClass: 'bg-teal-100 text-teal-700 border-teal-200' },
};


// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDurationShort(seconds: number | null | undefined): string {
    if (!seconds || seconds <= 0) return '-';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

function formatDurationVerbose(totalSeconds: number | null | undefined): string {
    if (!totalSeconds || totalSeconds <= 0) return '0 s';
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

function formatPercent(value: number): string {
    return new Intl.NumberFormat(currentLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value) + ' %';
}

function pctStr(num: number, denom: number): string {
    if (!denom || denom === 0) return formatPercent(0);
    return formatPercent((num / denom) * 100);
}

// ── Sub-components ───────────────────────────────────────────────────────────

interface StatCardProps {
  icon: IconType;
  label: string;
  value: string | number;
  sub?: string;
  iconColorClass: string;
  iconBgClass: string;
}

function StatCard({ icon: Icon, label, value, sub, iconColorClass, iconBgClass }: StatCardProps) {
    return (
        <div className="bg-card rounded-xl border border-border p-5 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center ${iconBgClass}`}>
                <Icon className={`w-5 h-5 ${iconColorClass}`} />
            </div>
            <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-tight truncate">{value}</p>
                <p className="text-xs text-muted-foreground leading-snug">{label}</p>
                {sub && <p className="text-xs text-muted-foreground/70 leading-snug">{sub}</p>}
            </div>
        </div>
    );
}

function OutcomeBreakdown({ calls }: { calls: CallShape[] }) {
    const { t } = useTranslation('dialer');
    const counts = useMemo(() => {
        const map: Record<string, number> = {};
        for (const call of calls) {
            const key = call.call_outcome;
            if (!key) continue;
            map[key] = (map[key] || 0) + 1;
        }
        return Object.entries(map).sort((a, b) => b[1] - a[1]);
    }, [calls]);

    if (counts.length === 0) return null;

    return (
        <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">{t('dialer:clientPortalView.outcomeBreakdown')}</h3>
                <span className="ml-auto text-xs text-muted-foreground">{t('dialer:clientPortalView.callsTotal', { count: calls.length })}</span>
            </div>
            <div className="flex flex-wrap gap-2">
                {counts.map(([outcome, count]) => {
                    const cfg = OUTCOME_CONFIG[outcome];
                    const label = outcomeLabel(outcome, t);
                    const badgeClass = cfg?.badgeClass ?? 'bg-muted text-muted-foreground border-border';
                    const pct = calls.length > 0
                        ? ((count / calls.length) * 100).toFixed(0)
                        : '0';
                    return (
                        <div
                            key={outcome}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${badgeClass}`}
                        >
                            <span className="text-base font-bold leading-none">{count}</span>
                            <span>{label}</span>
                            <span className="opacity-60">{pct} %</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function OutcomeBadge({ outcome }: { outcome: string | null | undefined }) {
    const { t } = useTranslation('dialer');
    const cfg = OUTCOME_CONFIG[outcome];
    if (!cfg) {
        return (
            <span className="text-xs text-muted-foreground/60">{outcome ? outcomeLabel(outcome, t) : '-'}</span>
        );
    }
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${cfg.badgeClass}`}>
            {outcomeLabel(outcome, t)}
        </span>
    );
}

/**
 * Which lists performed best: one row per list, most meetings first. A row
 * click narrows the whole portal to that list; clicking it again widens back.
 */
function ListPerformanceCard({
    rows,
    selected,
    onSelect,
}: {
    rows: ListPerformanceRow[];
    selected: string;
    onSelect: (list: string) => void;
}) {
    const { t } = useTranslation('dialer');
    if (rows.length < 2) return null;
    const best = Math.max(...rows.map((r) => r.meetingRate), 0);

    return (
        <div className="bg-card rounded-xl border border-border">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">{t('dialer:clientPortalView.byList.title')}</h3>
                <span className="ml-auto text-xs text-muted-foreground">{t('dialer:clientPortalView.byList.hint')}</span>
            </div>
            <div className="relative overflow-x-auto rounded-b-xl">
                <table className="w-full text-sm" data-testid="table-portal-lists">
                    <thead>
                        <tr className="border-b border-border bg-muted/40">
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">{t('dialer:clientPortalView.byList.columns.list')}</th>
                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground whitespace-nowrap">{t('dialer:clientPortalView.byList.columns.called')}</th>
                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground whitespace-nowrap">{t('dialer:clientPortalView.byList.columns.calls')}</th>
                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground whitespace-nowrap">{t('dialer:clientPortalView.byList.columns.reachRate')}</th>
                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground whitespace-nowrap">{t('dialer:clientPortalView.byList.columns.meetings')}</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap w-48">{t('dialer:clientPortalView.byList.columns.meetingRate')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                        {rows.map((r) => {
                            const active = selected === r.list;
                            return (
                                <tr
                                    key={r.list}
                                    onClick={() => onSelect(active ? 'all' : r.list)}
                                    className={`cursor-pointer transition-colors ${active ? 'bg-accent/60' : 'hover:bg-accent/40'}`}
                                    aria-selected={active}
                                >
                                    <td className="px-4 py-3 max-w-[18rem]">
                                        <p className="font-medium text-foreground truncate" title={r.list}>{r.list}</p>
                                    </td>
                                    <td className="px-4 py-3 text-right text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                                        {formatNumber(r.called)} / {formatNumber(r.contacts)}
                                    </td>
                                    <td className="px-4 py-3 text-right text-xs tabular-nums text-muted-foreground">{formatNumber(r.calls)}</td>
                                    <td className="px-4 py-3 text-right text-xs tabular-nums text-muted-foreground">
                                        {r.called > 0 ? formatPercent(r.reachRate) : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">{formatNumber(r.meetings)}</td>
                                    <td className="px-4 py-3">
                                        {r.called > 0 ? (
                                            <div className="flex items-center gap-2">
                                                <div className="h-1.5 flex-1 rounded-full overflow-hidden" style={{ background: 'var(--vf-surface-3)' }}>
                                                    <div
                                                        className="h-full rounded-full"
                                                        style={{ width: `${best > 0 ? (r.meetingRate / best) * 100 : 0}%`, background: 'var(--vf-accent)' }}
                                                    />
                                                </div>
                                                <span className="text-xs tabular-nums text-muted-foreground w-14 text-right">{formatPercent(r.meetingRate)}</span>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-muted-foreground/70">{t('dialer:clientPortalView.byList.notCalled')}</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function RecentCallsTable({ calls }: { calls: CallShape[] }) {
    const { t } = useTranslation('dialer');
    const recent = useMemo(
        () => [...calls]
            .sort(
                (a, b) =>
                    new Date(b.started_at || b.created_date || 0).getTime() -
                    new Date(a.started_at || a.created_date || 0).getTime(),
            )
            .slice(0, 20),
        [calls],
    );

    if (recent.length === 0) {
        return (
            <div className="bg-card rounded-xl border border-border">
                <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">{t('dialer:clientPortalView.recentCalls')}</h3>
                </div>
                <div className="flex flex-col items-center justify-center py-14 gap-3">
                    <Phone className="w-10 h-10 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">{t('dialer:clientPortalView.noCallsToDisplay')}</p>
                </div>
            </div>
        );
    }

    // #17 -- les titres de colonnes doivent rester visibles au defilement.
    // `overflow-hidden` sur la carte et `overflow-x-auto` sur la zone de table
    // creaient un conteneur de defilement sans hauteur contrainte : le sticky
    // s'y accrochait sans jamais bouger. On donne a la zone une hauteur
    // maximale et un defilement vertical reel. Le titre de la carte
    // ("Appels recents") reste au-dessus, hors de la zone qui defile.
    // Le MENU, lui, est deja fige : il vit hors du conteneur defilant de la
    // page (mesure : 0 px de deplacement pour 1099 px de defilement) -- rien a
    // corriger de ce cote.
    return (
        <div className="bg-card rounded-xl border border-border">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">{t('dialer:clientPortalView.recentCalls')}</h3>
                <span className="ml-auto text-xs text-muted-foreground">
                    {t('dialer:clientPortalView.lastCount', { count: recent.length })}
                </span>
            </div>
            <div className="relative overflow-x-auto overflow-y-auto max-h-[calc(100vh-22rem)] rounded-b-xl">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border bg-muted/40">
                            <th className="sticky top-0 z-10 bg-muted px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap">
                                {t('dialer:clientPortalView.columns.date')}
                            </th>
                            <th className="sticky top-0 z-10 bg-muted px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                                {t('dialer:clientPortalView.columns.prospect')}
                            </th>
                            <th className="sticky top-0 z-10 bg-muted px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap">
                                {t('dialer:clientPortalView.columns.outcome')}
                            </th>
                            <th className="sticky top-0 z-10 bg-muted px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground whitespace-nowrap">
                                {t('dialer:clientPortalView.columns.duration')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                        {recent.map((call) => (
                            <tr
                                key={call.id}
                                className="hover:bg-accent/40 transition-colors"
                            >
                                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap font-mono">
                                    {formatShortDate(call.started_at || call.created_date)}
                                </td>
                                <td className="px-4 py-3">
                                    <p className="font-medium text-foreground leading-tight">
                                        {call.prospect_name || <span className="text-muted-foreground/60 italic">—</span>}
                                    </p>
                                    {call.prospect_company && (
                                        <p className="text-xs text-muted-foreground leading-tight">
                                            {call.prospect_company}
                                        </p>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <OutcomeBadge outcome={call.call_outcome} />
                                </td>
                                <td className="px-4 py-3 text-right text-xs font-mono text-muted-foreground whitespace-nowrap">
                                    {formatDurationShort(call.duration_seconds)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

interface ClientPortalViewProps {
  /** The active customer's name (W4, quick win 12: the agency's email was shown as if the report were shared with it). */
  clientName?: string | null;
  /** Actions next to the title (« Copier le lien », « Voir comme le client »). */
  actions?: React.ReactNode;
  allCalls?: CallShape[];
  prospectQueue?: ProspectShape[];
  /** Prospects grouped by list (their source), already scoped to the customer. */
  prospectsBySource?: Record<string, ProspectShape[]>;
}

export default function ClientPortalView({
    clientName,
    actions,
    allCalls: everyCall = [],
    prospectQueue: everyProspect = [],
    prospectsBySource = {},
}: ClientPortalViewProps) {
    const { t } = useTranslation('dialer');
    // 'all' or one list name. Everything below the selector follows it.
    const [list, setList] = useState('all');

    const rows = useMemo(() => listPerformance(prospectsBySource, everyCall), [prospectsBySource, everyCall]);
    const sources = useMemo(() => rows.map((r) => r.list), [rows]);
    const completion = useMemo(() => {
        const result: Record<string, { called: number; total: number; pct: number }> = {};
        let called = 0;
        let total = 0;
        for (const r of rows) {
            result[r.list] = { called: r.called, total: r.contacts, pct: r.contacts > 0 ? Math.round((r.called / r.contacts) * 100) : 0 };
            called += r.called;
            total += r.contacts;
        }
        result.__all = { called, total, pct: total > 0 ? Math.round((called / total) * 100) : 0 };
        return result;
    }, [rows]);

    // A list that disappears (customer switched, list deleted) falls back to all.
    const activeList = list !== 'all' && prospectsBySource[list] ? list : 'all';
    const prospectQueue = activeList === 'all' ? everyProspect : prospectsBySource[activeList] ?? [];
    const allCalls = useMemo(
        () => (activeList === 'all' ? everyCall : callsOfList(prospectsBySource[activeList] ?? [], everyCall)),
        [activeList, everyCall, prospectsBySource],
    );

    const stats = useMemo(() => {
        const total = allCalls.length;

        const connected = allCalls.filter(
            (c) => c.call_outcome != null && CONNECTED_OUTCOMES.has(c.call_outcome),
        ).length;

        const meetings = allCalls.filter(hasMeetingBooked).length;

        const contactRate = total > 0 ? (connected / total) * 100 : 0;

        const callsWithDuration = allCalls.filter(
            (c) => c.duration_seconds && c.duration_seconds > 0,
        );
        const avgDuration =
            callsWithDuration.length > 0
                ? callsWithDuration.reduce((sum, c) => sum + (c.duration_seconds || 0), 0) /
                  callsWithDuration.length
                : 0;

        return { total, connected, meetings, contactRate, avgDuration };
    }, [allCalls]);

    const hasData = allCalls.length > 0;

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold text-foreground">
                        {clientName ? t('dialer:clientPortalView.dashboardTitleFor', { name: clientName }) : t('dialer:clientPortalView.dashboardTitle')}
                    </h2>
                </div>
                <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                    {actions}
                    {sources.length > 1 && (
                        <ListSelect sources={sources} completion={completion} value={activeList} onChange={setList} />
                    )}
                    {hasData && (
                        <Badge variant="secondary" className="flex-shrink-0">
                            {t('dialer:clientPortalView.callsTotalBadge', { count: allCalls.length })}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={Phone}
                    label={t('dialer:clientPortalView.totalCalls')}
                    value={stats.total}
                    sub={
                        prospectQueue.length > 0
                            ? t('dialer:clientPortalView.prospectsInQueue', { count: prospectQueue.length })
                            : undefined
                    }
                    iconColorClass="text-sky-600"
                    iconBgClass="bg-sky-50"
                />
                <StatCard
                    icon={TrendingUp}
                    label={t('dialer:clientPortalView.contactRate')}
                    value={hasData ? formatPercent(stats.contactRate) : '—'}
                    sub={
                        hasData
                            ? t('dialer:clientPortalView.contactsEstablished', { count: stats.connected })
                            : t('dialer:clientPortalView.noCalls')
                    }
                    iconColorClass="text-emerald-600"
                    iconBgClass="bg-emerald-50"
                />
                <StatCard
                    icon={Calendar}
                    label={t('dialer:clientPortalView.meetingsBooked')}
                    value={stats.meetings}
                    sub={
                        hasData
                            ? pctStr(stats.meetings, stats.total) + ' ' + t('dialer:clientPortalView.ofCallsSuffix')
                            : undefined
                    }
                    iconColorClass="text-violet-600"
                    iconBgClass="bg-violet-50"
                />
                <StatCard
                    icon={Clock}
                    label={t('dialer:clientPortalView.averageDuration')}
                    value={hasData ? formatDurationVerbose(Math.round(stats.avgDuration)) : '—'}
                    sub={t('dialer:clientPortalView.perConnectedCall')}
                    iconColorClass="text-amber-600"
                    iconBgClass="bg-amber-50"
                />
            </div>

            {/* Empty state when no calls at all */}
            {!hasData && (
                <div className="bg-card rounded-xl border border-border flex flex-col items-center justify-center py-16 gap-3">
                    <Users className="w-10 h-10 text-muted-foreground/30" />
                    <p className="text-sm font-medium text-muted-foreground">
                        {t('dialer:clientPortalView.noCallsYet')}
                    </p>
                    <p className="text-xs text-muted-foreground/70">
                        {t('dialer:clientPortalView.statsWillAppear')}
                    </p>
                </div>
            )}

            {/* Which lists performed best */}
            {everyCall.length > 0 && <ListPerformanceCard rows={rows} selected={activeList} onSelect={setList} />}

            {/* Outcomes breakdown */}
            {hasData && <OutcomeBreakdown calls={allCalls} />}

            {/* Recent calls table */}
            {hasData && <RecentCallsTable calls={allCalls} />}
        </div>
    );
}
